/**
 * Header search destinations — align with {@link AppSidebar} visibility:
 * - Trend (`/trends`): staff preview only; gated by `showEvolveNav` (`user.show_evolve`).
 * - Evolve-only paths (Performance, Brand Lift, MMM, Awards): gated by `showEvolveNav`
 *   (API `show_evolve` staff flag).
 * - `showCowork === false`: Home, Content Hub, Discover → Creators, Campaigns list/calendar/new, optional last-campaign shortcuts.
 * - `showCowork === true`: full cowork catalog additions (Messages, Brand Pulse, etc.).
 */

export type HeaderSearchCatalogEntry = {
  id: string;
  labelKey: string;
  path: string;
  /** Lowercase ASCII tokens for matching alongside translated label */
  keywords: string[];
  /** When set (e.g. last campaign title), use for display and recents instead of translating {@link labelKey} */
  resolvedLabel?: string;
};

/** `/campaigns/:id/...` — not `new` or `calendar` */
export function extractCampaignIdFromPath(path: string): string | null {
  const clean = path.split('?')[0] ?? path;
  if (!clean.startsWith('/campaigns/')) return null;
  const parts = clean.split('/').filter(Boolean);
  if (parts[0] !== 'campaigns') return null;
  const seg = parts[1];
  if (!seg || seg === 'new' || seg === 'calendar') return null;
  return seg;
}

function clientVisibleCatalog(
  lastCampaignPath: string | null,
  lastCampaignDisplayName: string | null
): HeaderSearchCatalogEntry[] {
  const out: HeaderSearchCatalogEntry[] = [
    {
      id: 'dashboard',
      labelKey: 'headerSearch.actions.dashboard',
      path: '/',
      keywords: ['home', 'start', 'dashboard', 'main'],
    },
    {
      id: 'content-hub',
      labelKey: 'headerSearch.actions.contentHub',
      path: '/content-hub',
      keywords: ['content', 'hub', 'library', 'media'],
    },
    {
      id: 'creators',
      labelKey: 'headerSearch.actions.creators',
      path: '/creators',
      keywords: ['creators', 'influencers', 'discover', 'discovery'],
    },
    {
      id: 'campaigns',
      labelKey: 'headerSearch.actions.campaigns',
      path: '/campaigns',
      keywords: ['campaigns', 'campaign', 'all', 'list'],
    },
  ];

  const cid = lastCampaignPath ? extractCampaignIdFromPath(lastCampaignPath) : null;
  const lastPath = lastCampaignPath?.split('?')[0] ?? '';
  if (cid) {
    out.push({
      id: 'last-campaign',
      labelKey: 'nav.lastCampaign',
      path: lastPath,
      keywords: ['last', 'recent', 'campaign', 'current'],
      resolvedLabel: lastCampaignDisplayName?.trim() || undefined,
    });
  }

  out.push(
    {
      id: 'campaign-calendar',
      labelKey: 'headerSearch.actions.campaignCalendar',
      path: '/campaigns/calendar',
      keywords: ['calendar', 'schedule', 'events', 'dates', 'deadlines'],
    },
    {
      id: 'new-campaign',
      labelKey: 'headerSearch.actions.newCampaign',
      path: '/campaigns/new',
      keywords: ['new', 'create', 'campaign', 'planner'],
    }
  );

  if (cid) {
    const base = `/campaigns/${encodeURIComponent(cid)}`;
    out.push(
      {
        id: 'campaign-overview',
        labelKey: 'headerSearch.actions.campaignOverview',
        path: `${base}/overview`,
        keywords: ['overview', 'summary', 'campaign'],
      },
      {
        id: 'campaign-profiles',
        labelKey: 'headerSearch.actions.campaignProfiles',
        path: `${base}/profiles`,
        keywords: ['profiles', 'suggestions', 'creators', 'curation'],
      },
      {
        id: 'campaign-content-approval',
        labelKey: 'headerSearch.actions.campaignContentApproval',
        path: `${base}/content-approval`,
        keywords: ['content', 'approval', 'drafts', 'review'],
      },
      {
        id: 'campaign-publications',
        labelKey: 'headerSearch.actions.campaignPublications',
        path: `${base}/publications`,
        keywords: ['publications', 'live', 'posts'],
      },
      {
        id: 'campaign-results',
        labelKey: 'headerSearch.actions.campaignResults',
        path: `${base}/results`,
        keywords: ['results', 'report', 'performance', 'summary'],
      }
    );
  }

  return out;
}

const TRENDS_HEADER_SEARCH_ENTRY: HeaderSearchCatalogEntry = {
  id: 'trends',
  labelKey: 'headerSearch.actions.trends',
  path: '/trends',
  keywords: ['trends', 'trending'],
};

/** Performance, Brand Lift, Marketing Mix Model, Awards — gated when {@link buildHeaderSearchCatalog} `showEvolveNav` is false. */
const EVOLVE_ONLY_HEADER_SEARCH_ENTRIES: HeaderSearchCatalogEntry[] = [
  {
    id: 'performance',
    labelKey: 'headerSearch.actions.performance',
    path: '/performance',
    keywords: ['performance', 'metrics', 'results'],
  },
  {
    id: 'brand-lift',
    labelKey: 'headerSearch.actions.brandLift',
    path: '/brand-lift',
    keywords: ['brand', 'lift'],
  },
  {
    id: 'mmm',
    labelKey: 'headerSearch.actions.mmm',
    path: '/marketing-mix-model',
    keywords: ['mmm', 'marketing', 'mix', 'model'],
  },
  {
    id: 'awards',
    labelKey: 'headerSearch.actions.awards',
    path: '/awards',
    keywords: ['awards', 'trophies', 'trophy'],
  },
];

const COWORK_ONLY_ENTRIES: HeaderSearchCatalogEntry[] = [
  {
    id: 'cowork',
    labelKey: 'headerSearch.actions.cowork',
    path: '/cowork',
    keywords: ['cowork', 'agents', 'agent', 'ai', 'team', 'tasks'],
  },
  {
    id: 'brand-pulse',
    labelKey: 'headerSearch.actions.brandPulse',
    // Brand OS → Pulse (the older /brand-pulse page is out of the navigation). Messages lives in the back office.
    path: '/agents/pulse',
    keywords: ['brand', 'pulse', 'monitor'],
  },
  {
    id: 'creator-lists',
    labelKey: 'headerSearch.actions.creatorLists',
    path: '/creator-lists',
    keywords: ['lists', 'creatorlists', 'creator', 'lists'],
  },
  {
    id: 'search',
    labelKey: 'headerSearch.actions.influencerSearch',
    path: '/creators/search',
    keywords: ['search', 'find', 'influencer', 'results'],
  },
  {
    id: 'audience-insights',
    labelKey: 'headerSearch.actions.audienceInsights',
    path: '/audience-insights',
    keywords: ['audience', 'insights', 'analytics'],
  },
  {
    id: 'news-monitor',
    labelKey: 'headerSearch.actions.newsMonitor',
    path: '/news-monitor',
    keywords: ['news', 'monitor', 'articles'],
  },
  {
    id: 'ad-studio',
    labelKey: 'headerSearch.actions.adStudio',
    path: '/ad-studio',
    keywords: ['ad', 'studio', 'creative'],
  },
  {
    id: 'video-ad-studio',
    labelKey: 'headerSearch.actions.videoAdStudio',
    path: '/ad-studio/video',
    keywords: ['video', 'ad', 'studio'],
  },
  {
    id: 'audio-dubbing',
    labelKey: 'headerSearch.actions.audioDubbing',
    path: '/ad-studio/dubbing',
    keywords: ['audio', 'dubbing', 'dub'],
  },
  {
    id: 'ambassador',
    labelKey: 'headerSearch.actions.ambassador',
    path: '/ambassador-hub',
    keywords: ['ambassador', 'hub'],
  },
  {
    id: 'drafts',
    labelKey: 'headerSearch.actions.drafts',
    path: '/drafts',
    keywords: ['drafts', 'draft'],
  },
  {
    id: 'casting',
    labelKey: 'headerSearch.actions.casting',
    path: '/casting',
    keywords: ['casting', 'cast'],
  },
  {
    id: 'strategies',
    labelKey: 'headerSearch.actions.strategies',
    path: '/strategies',
    keywords: ['strategies', 'strategy'],
  },
  {
    id: 'settings',
    labelKey: 'headerSearch.actions.settings',
    path: '/settings',
    keywords: ['settings', 'preferences', 'account'],
  },
  {
    id: 'team',
    labelKey: 'headerSearch.actions.team',
    path: '/team',
    keywords: ['team', 'members'],
  },
  {
    id: 'dev-mode',
    labelKey: 'headerSearch.actions.devMode',
    path: '/dev-mode',
    keywords: ['dev', 'developer', 'debug'],
  },
];

export function buildHeaderSearchCatalog(options: {
  showCowork: boolean;
  /** When false, omit Performance / Brand Lift / MMM / Awards from catalog. Default true. */
  showEvolveNav?: boolean;
  lastCampaignPath: string | null;
  lastCampaignDisplayName: string | null;
}): HeaderSearchCatalogEntry[] {
  const showEvolveNav = options.showEvolveNav ?? true;
  const base: HeaderSearchCatalogEntry[] = [
    ...clientVisibleCatalog(options.lastCampaignPath, options.lastCampaignDisplayName),
  ];
  if (showEvolveNav) {
    base.push(TRENDS_HEADER_SEARCH_ENTRY, ...EVOLVE_ONLY_HEADER_SEARCH_ENTRIES);
  }
  if (options.showCowork) {
    return [...base, ...COWORK_ONLY_ENTRIES];
  }
  return base;
}

/** Display label for a catalog row (recents + dropdown). */
export function headerSearchCatalogEntryLabel(
  entry: HeaderSearchCatalogEntry,
  translate: (key: string) => string
): string {
  const r = entry.resolvedLabel?.trim();
  if (r) return r;
  return translate(entry.labelKey);
}

export function filterHeaderSearchCatalog(
  rawQuery: string,
  translate: (key: string) => string,
  options: {
    includeDevMode?: boolean;
    showCowork: boolean;
    showEvolveNav?: boolean;
    lastCampaignPath: string | null;
    lastCampaignDisplayName: string | null;
  }
): HeaderSearchCatalogEntry[] {
  const includeDevMode = options.includeDevMode ?? false;
  const catalog = buildHeaderSearchCatalog({
    showCowork: options.showCowork,
    showEvolveNav: options.showEvolveNav,
    lastCampaignPath: options.lastCampaignPath,
    lastCampaignDisplayName: options.lastCampaignDisplayName,
  });
  const q = rawQuery.trim().toLowerCase();
  if (!q) return [];

  return catalog.filter((entry) => {
    if (entry.id === 'dev-mode' && !includeDevMode) return false;
    const label = headerSearchCatalogEntryLabel(entry, translate).toLowerCase();
    if (label.includes(q)) return true;
    const words = q.split(/\s+/).filter(Boolean);
    if (words.length > 0 && words.every((w) => label.includes(w))) return true;
    return entry.keywords.some(
      (k) => k.includes(q) || q.includes(k) || words.some((w) => k.includes(w) || w.includes(k))
    );
  });
}

const EVOLVE_ONLY_EXACT_NAV_PATHS = new Set([
  '/performance',
  '/brand-lift',
  '/marketing-mix-model',
  '/awards',
]);

/** Whether {@link clean} maps to Performance, Brand Lift, MMM, or Awards (detail under `/awards/...`). */
function isEvolveOnlyNavPath(clean: string): boolean {
  return EVOLVE_ONLY_EXACT_NAV_PATHS.has(clean) || clean.startsWith('/awards/');
}

/** Whether a stored recent nav path is still valid for the current user (sidebar visibility). */
export function isAllowedHeaderSearchNavPath(
  path: string,
  options: { isAdminJwt?: boolean; showCowork: boolean; showEvolveNav?: boolean }
): boolean {
  const { isAdminJwt = false, showCowork } = options;
  const allowEvolveNav = options.showEvolveNav ?? true;
  const clean = path.split('?')[0] ?? path;
  if (clean === '/dev-mode' && !isAdminJwt) return false;

  // Trends + Evolve paths require showEvolveNav (API show_evolve).
  if (!allowEvolveNav && isEvolveOnlyNavPath(clean)) return false;
  if (!allowEvolveNav && clean === '/trends') return false;

  if (showCowork) {
    if (clean === '/dev-mode') return isAdminJwt;
    return true;
  }

  if (allowEvolveNav && clean === '/trends') return true;

  if (allowEvolveNav && isEvolveOnlyNavPath(clean)) return true;

  // Client (no Cowork): same destinations as sidebar + any campaign detail URL.
  const clientAllowedRoots = new Set([
    '/',
    '/content-hub',
    '/creators',
    '/campaigns',
    '/campaigns/calendar',
    '/campaigns/new',
  ]);
  if (clientAllowedRoots.has(clean)) return true;
  if (clean.startsWith('/creators/search')) return true;
  if (clean.startsWith('/campaigns/')) {
    const parts = clean.split('/').filter(Boolean);
    if (parts[0] === 'campaigns') {
      if (parts[1] === 'new' || parts[1] === 'calendar') {
        return clean === '/campaigns/new' || clean === '/campaigns/calendar';
      }
      return parts.length >= 2;
    }
  }
  return false;
}
